import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { getMOCEventById } from '../../graphql/moc-events/get-moc-event-by-id';
import { getMocEventCategories } from '../../graphql/moc-events/get-moc-event-categories';
import { getMOCEventTypes } from '../../graphql/moc-events/get-moc-event-types';
import { MOCEventInput } from '../../model/crm/moc-event-input';
import { BaseAppSyncService } from '../base-app-sync.service';
import { queryForMOCEvents } from '../../graphql/moc-events/query-for-moc-events';
import { createMOCEvent } from '../../graphql/moc-events/create-moc-event';
import { deleteMOCEvent } from '../../graphql/moc-events/delete-moc-event';
import { createMOCEventAcknowledgement } from '../../graphql/moc-events/create-moc-event-acknowledgement';
import { updateMocEventAcknowledgement } from '../../graphql/moc-events/update-moc-event-acknowledgement';
import { MOCEventAckInput } from '../../model/crm/moc-event-ack-input';
import { queryForMOCEventCategories } from '../../graphql/moc-events/query-for-moc-event-categories';
import { updateMocEventCategory } from '../../graphql/moc-events/update-moc-event-category';
import { createMOCEventCategory } from '../../graphql/moc-events/create-moc-event-category';
import { deleteMOCEventCategory } from '../../graphql/moc-events/delete-moc-event-category';
import { MOCEventCatInput } from '../../model/admin/moc-event-cat-input';
import { allowMOCEventCategoryDelete } from '../../graphql/moc-events/allow-moc-event-category-delete';
import { getMOCEventsForAOR } from '../../graphql/moc-events/get-moc-events-for-aor';
import { getMOCEventByHistoryId } from '../../graphql/moc-events/get-moc-event-by-history-id';
import { AuthService } from '../auth.service';
import {
  MOCEventCategory,
  MOCEventCategoryConnection,
} from 'src/app/model/crm/moc-event-category';
import { MocEventBuilderService } from '../builder/moc-event-builder.service';
import { MOCEventAcknowledgement } from 'src/app/model/crm/moc-event-acknowledgement';
import { MOCEventType } from 'src/app/model/crm/moc-event-type';
import { MOCEvent, MOCEventConnection } from 'src/app/model/crm/moc-event';

@Injectable({
  providedIn: 'root',
})
export class MocEventApiService extends BaseAppSyncService {
  protected _auth = inject(AuthService);
  protected _mocBuilder = inject(MocEventBuilderService);

  getMOCEventTypes(): Observable<MOCEventType[]> {
    return this.query(
      getMOCEventTypes,
      {},
      (items: any[]) => items.map((i) => this._mocBuilder.buildMOCEventType(i)),
      'getMOCEventTypes',
      'cache-first'
    );
  }

  getMOCEventCategories(): Observable<MOCEventCategoryConnection> {
    return this.query(
      getMocEventCategories,
      {
        limit: 100000,
        page: 1,
        query: {
          name: null,
          enabled: 'true',
          businessUnits: null,
        },
        sort: {
          name: 'ASC',
        },
      },
      (i: any) => this._mocBuilder.buildMOCEventCategoryConnection(i),
      'queryForMOCEventCategories',
      'cache-first'
    );
  }

  queryForMOCEvents(
    limit: number,
    page: number,
    query: any,
    sort: any
  ): Observable<MOCEventConnection> {
    return this.query(
      queryForMOCEvents,
      { limit, page, query, sort },
      (i: any) => this._mocBuilder.buildMOCEventConnection(i),
      'queryForMOCEvents'
    );
  }

  getMOCEventById(id: string): Observable<MOCEvent> {
    return this.query(
      getMOCEventById,
      { id },
      (i: any) => this._mocBuilder.buildMOCEvent(i),
      'getMOCEventById'
    );
  }

  getMOCEventByHistoryId(id: string, historyId: number): Observable<MOCEvent> {
    return this.query(
      getMOCEventByHistoryId,
      { id, historyId },
      (i: any) => this._mocBuilder.buildMOCEvent(i),
      'getMOCEventByHistoryId'
    );
  }

  createMOCEvent(input: MOCEventInput): Observable<MOCEvent> {
    return this.mutate(
      createMOCEvent,
      { input },
      (i: any) => this._mocBuilder.buildMOCEvent(i),
      'createMOCEvent'
    );
  }

  deleteMOCEvent(id: String): Observable<boolean> {
    return this.mutate(deleteMOCEvent, { id }, (i: any) => i, 'deleteMOCEvent');
  }

  createMOCEventAcknowledgement(
    input: MOCEventAckInput
  ): Observable<MOCEventAcknowledgement> {
    return this.mutate(
      createMOCEventAcknowledgement,
      { input },
      (i: any) => this._mocBuilder.buildMocEventAcknowledgement(i),
      'createMOCEventAcknowledgement'
    );
  }

  updateMocEventAcknowledgement(
    id: string,
    changes: MOCEventAckInput
  ): Observable<MOCEventAcknowledgement> {
    return this.mutate(
      updateMocEventAcknowledgement,
      { id, changes },
      (i: any) => this._mocBuilder.buildMocEventAcknowledgement(i),
      'updateMocEventAcknowledgement'
    );
  }

  queryForMOCEventCategories(
    limit: number,
    page: number,
    query: any,
    sort: any
  ): Observable<MOCEventCategoryConnection> {
    return this.query(
      queryForMOCEventCategories,
      { limit, page, query, sort },
      (i: any) => this._mocBuilder.buildMOCEventCategoryConnection(i),
      'queryForMOCEventCategories'
    );
  }

  createMOCEventCategory(
    input: MOCEventCatInput | null
  ): Observable<MOCEventCategory> {
    return this.mutate(
      createMOCEventCategory,
      {
        name: input?.name,
        shiftGroupId: input?.shiftGroupId,
        enabled: input?.enabled,
        businessUnits: { businessUnits: input?.businessUnits },
      },
      (i: any) => this._mocBuilder.buildMOCEventCategory(i),
      'createMOCEventCategory'
    );
  }

  updateMocEventCategory(
    id: string,
    changes: MOCEventCatInput | null
  ): Observable<MOCEventCategory> {
    return this.mutate(
      updateMocEventCategory,
      { id, changes },
      (i: any) => this._mocBuilder.buildMOCEventCategory(i),
      'updateMocEventCategory'
    );
  }

  allowMOCEventCategoryDelete(id: string): Observable<boolean> {
    return this.query(
      allowMOCEventCategoryDelete,
      { id },
      (i: any) => i.value,
      'allowMOCEventCategoryDelete'
    );
  }

  deleteMOCEventCategory(id: String): Observable<boolean> {
    return this.mutate(
      deleteMOCEventCategory,
      { id },
      (i: any) => i,
      'deleteMOCEventCategory'
    );
  }

  getMOCEventsForAOR(
    aorId: string,
    turnoverIncomingCompleted: Date | null
  ): Observable<any[]> {
    return this.query(
      getMOCEventsForAOR,
      {
        aorId,
        turnoverIncomingCompleted:
          turnoverIncomingCompleted?.toISOString() ?? null,
      },
      (i: any) => i,
      'getMOCEventsForAOR'
    );
  }
}
